/**
 * Ce que le client a le droit de savoir du stock.
 *
 * Règle posée le 24/08/2026 : le catalogue n'affiche PLUS les quantités. Un
 * chiffre exact n'aide personne (il est faux dès la vente suivante, et il expose
 * notre inventaire), alors que deux informations comptent vraiment pour décider :
 *   - « il n'en reste presque plus » → le client se dépêche ou change d'avis ;
 *   - « ce que tu as commandé n'est plus là » → dit au moment de commander,
 *     clairement, produit par produit (cf. POST /orders/check-availability).
 *
 * Les précommandes (pain) sortent du raisonnement : leur disponibilité est une
 * question de jour de fabrication, pas de stock en rayon.
 */

import type { Product } from '../types/ecommerce.types';

/**
 * Seuils du signal « il n'en reste presque plus ».
 *
 * Choisis sur le catalogue réel de production (24/08/2026, 1084 produits en
 * rayon) : à 5 pièces, 526 produits sur 927 porteraient le badge — un badge sur
 * un produit vendable sur deux ne veut plus rien dire. À 2 pièces, 144 (16 %) —
 * assez rare pour être lu, et c'est le moment où l'information change vraiment
 * la décision du client. Côté poids, 1 kg ne concerne que 11 produits sur 157.
 *
 * Deux constantes à ajuster si le catalogue change de nature.
 */
export const LOW_STOCK_PIECES = 2;
export const LOW_STOCK_KG = 1;

export type ShopStockState = 'preorder' | 'out' | 'low' | 'ok';

type StockLike = Pick<
  Product,
  'stock_quantity' | 'stock_display' | 'stock_backroom' | 'is_weighted' | 'preorder_enabled' | 'unit' | 'en_stock'
>;

/**
 * Le stock que la boutique doit regarder : rayon + RÉSERVE.
 *
 * C'est l'arithmétique de `getAvailableStock` côté serveur — celle qui décide
 * de ce que la commande sait vraiment servir, `deductStock` promouvant depuis
 * la réserve ce qui manque au rayon. La colonne `products.stock_quantity`, elle,
 * ne somme que le rayon : la liste du catalogue corrigeait déjà le tir, mais la
 * FICHE servait la colonne brute et déclarait « Épuisé pour le moment » sur des
 * articles bel et bien commandables (22 en production le 08/09/2026, dont douze
 * beurres salés dormant en réserve). Deux arithmétiques différentes pour un
 * seul mot faisaient disparaître de la marchandise réelle.
 */
function stockVendable(product: StockLike): number | null {
  // Catalogue public depuis le 06/10/2026 : le serveur ne transmet plus AUCUNE
  // quantité à un visiteur, seulement `en_stock`. Il fait foi quand il est là.
  if (typeof product.en_stock === 'boolean') return product.en_stock ? Number.POSITIVE_INFINITY : 0;
  const candidats: number[] = [];

  const rayon = Number(product.stock_display);
  const reserve = Number(product.stock_backroom);
  if (Number.isFinite(rayon) && Number.isFinite(reserve)) candidats.push(rayon + reserve);

  const brut = Number(product.stock_quantity);
  if (Number.isFinite(brut)) candidats.push(brut);

  if (candidats.length === 0) return null;

  // Le PLUS élevé, jamais le plus bas. La table `products` traîne deux colonnes
  // mortes `stock_display`/`stock_backroom` que plus rien n'alimente (à 0
  // depuis toujours) : une réponse construite en recopiant la ligne produit les
  // sert donc à 0 tout en portant un `stock_quantity` juste. Prendre leur somme
  // au pied de la lettre déclarerait « Épuisé » tout un catalogue pourtant en
  // rayon. Se tromper vers le haut n'a pas ce prix : la disponibilité réelle
  // est revérifiée article par article au moment de commander
  // (POST /orders/check-availability), et c'est là qu'elle se dit.
  return Math.max(...candidats);
}

export function getShopStockState(product: StockLike): ShopStockState {
  if (product.preorder_enabled) return 'preorder';

  const stock = stockVendable(product);
  // Stock inconnu (catalogue partiel, produit hors périmètre) : ne rien affirmer.
  if (stock === null) return 'ok';

  const qty = stock;
  if (qty <= 0) return 'out';

  const weighted = product.is_weighted === true || product.unit === 'kg';
  return qty <= (weighted ? LOW_STOCK_KG : LOW_STOCK_PIECES) ? 'low' : 'ok';
}

/**
 * Quantité restante en français lisible. Le poids passe en grammes sous 1 kg —
 * « il reste 300 g » se comprend mieux que « il reste 0,3 kg ».
 */
export function formatRemaining(quantity: number, product: Pick<Product, 'is_weighted' | 'unit'>): string {
  const weighted = product.is_weighted === true || product.unit === 'kg';
  if (!weighted) {
    const n = Math.floor(quantity);
    return `${n} ${n > 1 ? 'pièces' : 'pièce'}`;
  }
  if (quantity < 1) return `${Math.round(quantity * 1000)} g`;
  return `${quantity.toFixed(quantity % 1 === 0 ? 0 : 1).replace('.', ',')} kg`;
}
